import BrandHeroBlock from '@/blocks/BrandHeroBlock';
import BreadcrumbsBlock from '@/blocks/BreadcrumbsBlock';
import TextImageAsideBlock from '@/blocks/TextImageAsideBlock';
import LogoBackgroundSilhouette from '@/components/LogoBackgroundSilhouette';
import { MEDIA_URL, REVALIDATE_TIME_IN_SEC } from '@/constants/env';
import { fadeIn, fadeInUp } from '@/constants/motions';
import { pages } from '@/content';
import FullWidthLayout from '@/layouts/FullWidthLayout';
import ButtonLink from '@/ui/ButtonLink';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import graphql from '@/lib/graphql';
import mainLayout from '@/graphql/queries/mainLayout';
import Markdown from 'react-markdown';
import page from '@/graphql/queries/page';

export default function EngineeringPage({ mainLayout, page }) {
  const attributes = page?.attributes || {};
  const contentSection = attributes.contentSection || [];
  const metaData = attributes.metaData || {};
  return (
    <FullWidthLayout mainLayout={mainLayout} metaData={metaData}>
      {contentSection[0]?.heroImage?.data?.attributes?.url ? (
        <BrandHeroBlock
          image={MEDIA_URL.concat(contentSection[0].heroImage.data.attributes.url)}
          title={contentSection[0].heroTitle}
        />
      ) : null}
      <BreadcrumbsBlock items={[pages.home, pages.engineering]} />
      {contentSection[1]?.textImageAsideImage?.data?.attributes?.url ? (
        <TextImageAsideBlock
          image={MEDIA_URL.concat(contentSection[1].textImageAsideImage.data.attributes.url)}
          imageDirection="right"
          subtitle={contentSection[1].textImageAsideSubtitle}
          title={contentSection[1].textImageAsideTitle}
        >
          <Markdown>{contentSection[1].textImageAsideText}</Markdown>
        </TextImageAsideBlock>
      ) : null}
      <motion.section
        className="relative my-16 overflow-hidden bg-primary-700 py-16 px-4 text-white"
        initial="hidden"
        transition={{
          ...fadeIn.transition,
          when: 'beforeChildren',
          staggerChildren: 0.4,
        }}
        variants={fadeIn}
        viewport={fadeIn.viewport}
        whileInView="visible"
      >
        <div className="absolute left-0 top-0 hidden opacity-10 lg:block">
          <LogoBackgroundSilhouette />
        </div>
        <div className="container-screen relative">
          <div className="flex flex-wrap items-center gap-8">
            <motion.div className="w-full lg:w-1/3" variants={fadeInUp}>
              <h4 className="text-lg opacity-60">
                {contentSection[2]?.textImageAsideSubtitle || ''}
              </h4>
              <h3 className="mt-4 text-2xl md:text-3xl">
                {contentSection[2]?.textImageAsideTitle || ''}
              </h3>
              <p className="mt-6">
                <Markdown>{contentSection[2]?.textImageAsideText || ''}</Markdown>
              </p>
            </motion.div>
            <div className="flex flex-wrap gap-4 lg:flex-1 lg:gap-8">
              {(contentSection[3]?.engineeringServices || []).map(
                ({ icon, title, text, linkText, linkUrl }, i) => (
                  <motion.div
                    key={i}
                    className="flex flex-col items-center gap-4 rounded-lg border-2 border-white p-8 text-center sm:flex-1"
                    variants={fadeInUp}
                  >
                    <Icon height="64" icon={icon} />
                    <h3 className="text-xl">{title}</h3>
                    <p className="opacity-80">{text}</p>
                    <div className="mt-auto">
                      <ButtonLink
                        color="inverted"
                        href={linkUrl}
                        icon={<Icon height="24" icon="fluent:chevron-circle-right-16-filled" />}
                      >
                        {linkText}
                      </ButtonLink>
                    </div>
                  </motion.div>
                )
              )}
            </div>
          </div>
        </div>
      </motion.section>
    </FullWidthLayout>
  );
}

export async function getStaticProps() {
  const { data: mainLayoutData } = await graphql(mainLayout);

  const { data: pageData } = await graphql(page, { slug: 'engineering' });

  return {
    props: {
      mainLayout: mainLayoutData.mainLayout.data,
      page: pageData.pages.data[0],
    },
    revalidate: parseInt(REVALIDATE_TIME_IN_SEC), // In seconds
  };
}
